import axios from "axios";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import DataTable from "../components/DataTable";
import {
  MODULO_COBROS,
  authConfig,
  badgeEstado,
  formatearFecha,
  formatearMoneda,
  manejarErrorSesion,
  nombreConcepto,
  nombreInstructor,
  obtenerModuloPorNombre,
  obtenerRolNombre,
  tienePermiso,
} from "../utils/cobros";

const escaparHtml = (valor = "") =>
  String(valor)
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");

const ListarPagos = () => {
  const navigate = useNavigate();
  const [pagos, setPagos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [procesandoId, setProcesandoId] = useState(null);

  const modulo = useMemo(() => obtenerModuloPorNombre(MODULO_COBROS), []);
  const puedeLeer = useMemo(() => tienePermiso(modulo, "LEER"), [modulo]);
  const puedeCrear = useMemo(() => tienePermiso(modulo, "CREAR"), [modulo]);
  const puedeActualizar = useMemo(() => tienePermiso(modulo, "ACTUALIZAR"), [modulo]);
  const puedeEliminar = useMemo(() => tienePermiso(modulo, "ELIMINAR"), [modulo]);
  const esInstructor = useMemo(() => obtenerRolNombre() === "INSTRUCTOR", []);
  const puedeRevisarPagos = puedeActualizar && !esInstructor;

  const getData = useCallback(async () => {
    if (!puedeLeer) {
      setPagos([]);
      return;
    }
    try {
      setLoading(true);
      const { data } = await axios.get("/api/pagos/listall", authConfig());
      if (!Array.isArray(data?.pagos)) throw new Error("Datos de pagos no válidos.");
      setPagos(data.pagos);
    } catch (error) {
      console.error("Error cargando pagos:", error);
      if (manejarErrorSesion(error, navigate)) return;
      Swal.fire("Error", error.response?.data?.message || "No se pudieron cargar los pagos.", "error");
      setPagos([]);
    } finally {
      setLoading(false);
    }
  }, [navigate, puedeLeer]);

  useEffect(() => {
    getData();
  }, [getData]);

  const verDetalle = useCallback((pago) => {
    const instructor = pago.instructorObjectId;
    const plan = pago.planObjectId;
    const origen = pago.suscripcionReferenciaObjectId;
    const resultado = pago.suscripcionObjectId;
    const comprobante = pago.comprobantePago
      ? `<div class="mt-3"><a href="${escaparHtml(pago.comprobantePago)}" target="_blank" rel="noreferrer" class="btn btn-outline-primary btn-sm"><i class="fa-solid fa-receipt me-2"></i>Ver comprobante</a></div>`
      : `<div class="mt-3 small text-secondary">Sin comprobante adjunto.</div>`;

    Swal.fire({
      title: "Detalle del pago",
      width: 700,
      html: `
        <div class="text-start">
          <div class="row g-3">
            <div class="col-12"><strong>Instructor:</strong><br/>${escaparHtml(nombreInstructor(instructor))}</div>
            <div class="col-md-6"><strong>Documento:</strong><br/>${escaparHtml(instructor?.documentoInstructor || "—")}</div>
            <div class="col-md-6"><strong>Concepto:</strong><br/>${escaparHtml(nombreConcepto(pago.conceptoPago))}</div>
            <div class="col-md-6"><strong>Plan:</strong><br/>${escaparHtml(plan?.nombrePlan || "—")}</div>
            <div class="col-md-6"><strong>Valor:</strong><br/>${escaparHtml(formatearMoneda(pago.valorPago, pago.moneda))}</div>
            <div class="col-md-6"><strong>Fecha pago:</strong><br/>${escaparHtml(formatearFecha(pago.fechaPago))}</div>
            <div class="col-md-6"><strong>Método:</strong><br/>${escaparHtml(pago.metodoPago || "—")}</div>
            <div class="col-md-6"><strong>Referencia:</strong><br/>${escaparHtml(pago.referenciaPago || "—")}</div>
            <div class="col-md-6"><strong>Estado:</strong><br/>${escaparHtml(pago.estadoPago || "—")}</div>
            <div class="col-12"><strong>Suscripción origen:</strong><br/>${escaparHtml(origen?._id || origen || "—")}</div>
            <div class="col-12"><strong>Período generado/afectado:</strong><br/>${escaparHtml(resultado?._id || resultado || "—")}</div>
            <div class="col-md-6"><strong>Inicio período:</strong><br/>${escaparHtml(formatearFecha(pago.fechaInicioPeriodo))}</div>
            <div class="col-md-6"><strong>Fin período:</strong><br/>${escaparHtml(formatearFecha(pago.fechaFinPeriodo))}</div>
            <div class="col-12"><strong>Observaciones:</strong><br/>${escaparHtml(pago.observaciones || "—")}</div>
            ${pago.motivoRechazo ? `<div class="col-12"><strong>Motivo rechazo:</strong><br/>${escaparHtml(pago.motivoRechazo)}</div>` : ""}
            ${pago.motivoAnulacion ? `<div class="col-12"><strong>Motivo anulación:</strong><br/>${escaparHtml(pago.motivoAnulacion)}</div>` : ""}
          </div>
          ${comprobante}
        </div>`,
      confirmButtonText: "Cerrar",
    });
  }, []);

  const aprobarPago = useCallback(
    async (pago, pedirConfirmacion = true) => {
      if (!puedeRevisarPagos) {
        Swal.fire("Sin permisos", "La aprobación de pagos corresponde a administración.", "warning");
        return;
      }
      if (String(pago.estadoPago).toUpperCase() !== "PENDIENTE") {
        Swal.fire("Operación no permitida", "Solo los pagos PENDIENTES pueden aprobarse.", "warning");
        return;
      }

      if (pedirConfirmacion) {
        const result = await Swal.fire({
          title: `¿Aprobar pago de ${nombreConcepto(pago.conceptoPago)}?`,
          text:
            String(pago.conceptoPago).toUpperCase() === "RENOVACION"
              ? "Se generará exactamente un nuevo período de renovación."
              : "Se activará la misma suscripción que está pendiente de pago.",
          icon: "question",
          showCancelButton: true,
          confirmButtonText: "Sí, aprobar",
          cancelButtonText: "Cancelar",
        });
        if (!result.isConfirmed) return;
      }

      try {
        setProcesandoId(pago._id);
        const { data } = await axios.put(`/api/pagos/aprobar/${pago._id}`, {}, authConfig());
        await Swal.fire({
          icon: "success",
          title: data?.message || "Pago aprobado correctamente",
          showConfirmButton: false,
          timer: 1900,
        });
        await getData();
      } catch (error) {
        console.error("Error aprobando pago:", error);
        if (manejarErrorSesion(error, navigate)) return;
        Swal.fire("Error", error.response?.data?.message || "No se pudo aprobar el pago.", "error");
      } finally {
        setProcesandoId(null);
      }
    },
    [getData, navigate, puedeRevisarPagos]
  );

  const rechazarPago = useCallback(
    async (pago) => {
      if (!puedeRevisarPagos) return;
      if (String(pago.estadoPago).toUpperCase() !== "PENDIENTE") return;

      const result = await Swal.fire({
        title: "Rechazar pago",
        input: "textarea",
        inputLabel: "Motivo del rechazo",
        inputPlaceholder: "Ejemplo: No se encontró la transferencia indicada.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Rechazar",
        cancelButtonText: "Cancelar",
        inputValidator: (value) => !String(value || "").trim() ? "Indique el motivo del rechazo." : undefined,
      });
      if (!result.isConfirmed) return;

      try {
        setProcesandoId(pago._id);
        const { data } = await axios.put(
          `/api/pagos/rechazar/${pago._id}`,
          { motivoRechazo: String(result.value || "").trim() },
          authConfig()
        );
        await Swal.fire({ icon: "success", title: data?.message || "Pago rechazado", showConfirmButton: false, timer: 1700 });
        await getData();
      } catch (error) {
        if (manejarErrorSesion(error, navigate)) return;
        Swal.fire("Error", error.response?.data?.message || "No se pudo rechazar el pago.", "error");
      } finally {
        setProcesandoId(null);
      }
    },
    [getData, navigate, puedeRevisarPagos]
  );

  const anularPago = useCallback(
    async (pago) => {
      if (!puedeRevisarPagos) return;
      if (String(pago.estadoPago).toUpperCase() !== "APROBADO") {
        Swal.fire("Operación no permitida", "Solo un pago APROBADO puede anularse.", "warning");
        return;
      }

      const result = await Swal.fire({
        title: "Anular pago aprobado",
        input: "textarea",
        inputLabel: "Motivo de anulación",
        inputPlaceholder: "Explique por qué debe anularse el pago...",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Anular pago",
        cancelButtonText: "Cancelar",
        inputValidator: (value) => !String(value || "").trim() ? "Indique el motivo de la anulación." : undefined,
      });
      if (!result.isConfirmed) return;

      const confirmar = await Swal.fire({
        title: "¿Confirmar anulación?",
        text: "El período activado o generado por este pago será cancelado.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Sí, anular pago",
        cancelButtonText: "Volver",
        confirmButtonColor: "#dc3545",
      });
      if (!confirmar.isConfirmed) return;

      try {
        setProcesandoId(pago._id);
        const { data } = await axios.put(
          `/api/pagos/anular/${pago._id}`,
          { motivoAnulacion: String(result.value || "").trim() },
          authConfig()
        );
        await Swal.fire({ icon: "success", title: data?.message || "Pago anulado", showConfirmButton: false, timer: 1700 });
        await getData();
      } catch (error) {
        if (manejarErrorSesion(error, navigate)) return;
        Swal.fire("Error", error.response?.data?.message || "No se pudo anular el pago.", "error");
      } finally {
        setProcesandoId(null);
      }
    },
    [getData, navigate, puedeRevisarPagos]
  );

  const revisarPago = useCallback(
    async (pago) => {
      if (!puedeRevisarPagos) return;
      if (String(pago.estadoPago).toUpperCase() !== "PENDIENTE") return;

      const instructor = pago.instructorObjectId;
      const plan = pago.planObjectId;
      const result = await Swal.fire({
        title: "Revisar pago",
        width: 650,
        icon: "question",
        html: `
          <div class="text-start border rounded-3 p-3 bg-light">
            <div class="mb-2"><span class="small text-secondary text-uppercase">Instructor</span><br/><strong>${escaparHtml(nombreInstructor(instructor))}</strong></div>
            <div class="row g-2">
              <div class="col-md-6"><span class="small text-secondary text-uppercase">Concepto</span><br/><strong>${escaparHtml(nombreConcepto(pago.conceptoPago))}</strong></div>
              <div class="col-md-6"><span class="small text-secondary text-uppercase">Plan</span><br/><strong>${escaparHtml(plan?.nombrePlan || "—")}</strong></div>
              <div class="col-md-6"><span class="small text-secondary text-uppercase">Valor</span><br/><strong>${escaparHtml(formatearMoneda(pago.valorPago, pago.moneda))}</strong></div>
              <div class="col-md-6"><span class="small text-secondary text-uppercase">Método</span><br/><strong>${escaparHtml(pago.metodoPago || "—")}</strong></div>
              <div class="col-12"><span class="small text-secondary text-uppercase">Referencia</span><br/><strong>${escaparHtml(pago.referenciaPago || "—")}</strong></div>
            </div>
          </div>
          <div class="mt-3 small text-secondary">Confirme que el dinero realmente fue recibido antes de aprobar.</div>`,
        showCancelButton: true,
        showDenyButton: true,
        confirmButtonText: '<i class="fa-solid fa-check me-2"></i>Aprobar',
        denyButtonText: '<i class="fa-solid fa-xmark me-2"></i>Rechazar',
        cancelButtonText: "Cerrar",
        reverseButtons: true,
      });

      if (result.isConfirmed) await aprobarPago(pago, false);
      if (result.isDenied) await rechazarPago(pago);
    },
    [aprobarPago, puedeRevisarPagos, rechazarPago]
  );

  const eliminarPago = useCallback(
    async (pago) => {
      if (!puedeEliminar) return;
      const estado = String(pago.estadoPago || "").toUpperCase();
      if (!["PENDIENTE", "RECHAZADO"].includes(estado)) return;

      const result = await Swal.fire({
        title: "¿Eliminar pago?",
        text: "Solo se eliminan registros pendientes o rechazados. Un pago aprobado debe anularse.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Sí, eliminar",
        cancelButtonText: "Cancelar",
        confirmButtonColor: "#dc3545",
      });
      if (!result.isConfirmed) return;

      try {
        setProcesandoId(pago._id);
        const { data } = await axios.delete(`/api/pagos/delete/${pago._id}`, authConfig());
        await Swal.fire({ icon: "success", title: data?.message || "Pago eliminado", showConfirmButton: false, timer: 1500 });
        await getData();
      } catch (error) {
        if (manejarErrorSesion(error, navigate)) return;
        Swal.fire("Error", error.response?.data?.message || "No se pudo eliminar el pago.", "error");
      } finally {
        setProcesandoId(null);
      }
    },
    [getData, navigate, puedeEliminar]
  );

  const dataTabla = useMemo(
    () => pagos.map((pago) => {
      const instructor = pago.instructorObjectId;
      const plan = pago.planObjectId;
      const estado = String(pago.estadoPago || "").toUpperCase();
      return {
        ...pago,
        documentoInstructor: instructor?.documentoInstructor || "—",
        instructorNombre: nombreInstructor(instructor),
        conceptoVisual: nombreConcepto(pago.conceptoPago),
        planNombre: plan?.nombrePlan || "—",
        valorFormateado: formatearMoneda(pago.valorPago, pago.moneda),
        fechaPagoFormateada: formatearFecha(pago.fechaPago),
        metodoPagoVisual: String(pago.metodoPago || "—").replaceAll("_", " "),
        referenciaPagoVisual: pago.referenciaPago || "—",
        estadoVisual: badgeEstado(estado),
        funciones: (
          <div className="d-flex justify-content-end gap-2 flex-wrap">
            <button className="btn btn-outline-primary btn-sm" type="button" title="Ver detalle" onClick={() => verDetalle(pago)}><i className="fa-solid fa-eye"></i></button>
            {puedeActualizar && estado === "PENDIENTE" && (
              <button className="btn btn-warning btn-sm" type="button" title="Editar" onClick={() => navigate(`/cobros/pagos/edit/${pago._id}`)}><i className="fa-solid fa-pen-to-square"></i></button>
            )}
            {puedeRevisarPagos && estado === "PENDIENTE" && (
              <button className="btn btn-success btn-sm" type="button" title="Revisar" onClick={() => revisarPago(pago)} disabled={procesandoId === pago._id}><i className="fa-solid fa-magnifying-glass-dollar"></i></button>
            )}
            {puedeRevisarPagos && estado === "APROBADO" && (
              <button className="btn btn-outline-danger btn-sm" type="button" title="Anular" onClick={() => anularPago(pago)} disabled={procesandoId === pago._id}><i className="fa-solid fa-ban"></i></button>
            )}
            {puedeEliminar && ["PENDIENTE", "RECHAZADO"].includes(estado) && (
              <button className="btn btn-danger btn-sm" type="button" title="Eliminar" onClick={() => eliminarPago(pago)} disabled={procesandoId === pago._id}><i className={`fa-solid ${procesandoId === pago._id ? "fa-spinner fa-spin" : "fa-trash"}`}></i></button>
            )}
          </div>
        ),
      };
    }),
    [pagos, puedeActualizar, puedeEliminar, puedeRevisarPagos, procesandoId, navigate, verDetalle, revisarPago, anularPago, eliminarPago]
  );

  const columns = useMemo(() => [
    { Header: "Documento", accessor: "documentoInstructor" },
    { Header: "Instructor", accessor: "instructorNombre" },
    { Header: "Concepto", accessor: "conceptoVisual" },
    { Header: "Plan", accessor: "planNombre" },
    { Header: "Valor", accessor: "valorFormateado" },
    { Header: "Fecha", accessor: "fechaPagoFormateada" },
    { Header: "Método", accessor: "metodoPagoVisual", hideHeaderOnMobile: true },
    { Header: "Referencia", accessor: "referenciaPagoVisual", hideHeaderOnMobile: true },
    { Header: "Estado", accessor: "estadoVisual" },
    { Header: "Acciones", accessor: "funciones" },
  ], []);

  if (!puedeLeer) {
    return <div className="container-fluid px-2 px-md-4"><div className="alert alert-warning">No tiene permisos para consultar pagos.</div></div>;
  }

  return (
    <div className="container-fluid px-2 px-md-4">
      <div className="bg-white border rounded-4 shadow-sm overflow-hidden">
        <div className="px-3 px-md-4 py-3 border-bottom bg-white">
          <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
            <div>
              <h5 className="mt-2 mb-1 fw-semibold">Pagos</h5>
              <small className="text-secondary d-block">Control de pagos por SUSCRIPCIÓN y RENOVACIÓN.</small>
              <div className="small text-secondary mt-2">Efectivo registrado por administración se aprueba automáticamente; otros métodos quedan pendientes de revisión.</div>
            </div>
            <div className="d-flex gap-2 flex-wrap">
              <button className="btn btn-outline-dark" type="button" onClick={() => navigate("/inicio")}><i className="fa-solid fa-house me-2"></i>Inicio</button>
              <button className="btn btn-outline-secondary" type="button" onClick={getData} disabled={loading}><i className={`fa-solid ${loading ? "fa-spinner fa-spin" : "fa-rotate"} me-2`}></i>Actualizar</button>
              {puedeCrear && (
                <button className="btn btn-dark" type="button" onClick={() => navigate("/cobros/pagos/nuevo")} disabled={loading}><i className="fa-solid fa-plus me-2"></i>Registrar pago</button>
              )}
            </div>
          </div>
        </div>

        <div>
          {loading ? (
            <div className="text-center text-secondary py-5"><div className="spinner-border" role="status"></div><div className="mt-3">Cargando pagos…</div></div>
          ) : dataTabla.length > 0 ? (
            <div className="overflow-auto"><DataTable columns={columns} data={dataTabla} /></div>
          ) : (
            <div className="text-center text-secondary py-5"><div className="display-6 mb-2">💳</div><div className="fw-semibold">No hay pagos registrados</div></div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ListarPagos;
